// src/components/user/Bookings.jsx
import React, { useState, useEffect } from 'react';
import { getUserBookings, cancelUserBooking } from '../../../api/booking';
import { getClientBookings } from '../../../api/user/serviceProvider/service-booking';
import './css/Bookings.css';
import { getUserPropertyBookings } from '../../../api/user/property/property';
import {
    FaWhatsapp,
    FaMapMarkerAlt,
    FaCalendarAlt,
    FaMoneyBillWave,
    FaUser,
    FaPhone,
    FaEnvelope,
    FaBuilding,
    FaHotel,
    FaExternalLinkAlt,
    FaBed,
    FaTools
} from 'react-icons/fa';

const Bookings = () => {
    const [bookings, setBookings] = useState([]);
    const [propertyBookings, setPropertyBookings] = useState([]);
    const [purchaseBookings, setPurchaseBookings] = useState([]);
    const [serviceBookings, setServiceBookings] = useState([]);
    const [propertyTabType, setPropertyTabType] = useState('rent'); // 'rent' or 'buy'
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('hotels');
    const [cancellingBooking, setCancellingBooking] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                // Fetch hotel bookings
                const hotelResponse = await getUserBookings();
                if (hotelResponse.success) {
                    setBookings(hotelResponse.data);
                }

                // Fetch property bookings
                const propertyResponse = await getUserPropertyBookings();
                console.log('Property Bookings:', propertyResponse);
                if (propertyResponse) {
                    setPropertyBookings(propertyResponse.propertyBookings || []);
                    setPurchaseBookings(propertyResponse.purchaseBookings || []);
                }

                // Fetch service bookings
                const serviceResponse = await getClientBookings();
                if (serviceResponse.success) {
                    setServiceBookings(serviceResponse.data || []);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleCancelBooking = async (hotelId, roomId, bookingId) => {
        if (!window.confirm('Are you sure you want to cancel this booking? This action cannot be undone.')) {
            return;
        }

        try {
            setCancellingBooking(bookingId);
            const response = await cancelUserBooking(hotelId, roomId, bookingId);

            if (response.success) {
                setBookings(prev => prev.filter(
                    booking => booking._id !== bookingId
                ));
                alert('Booking cancelled successfully');
            }
        } catch (err) {
            setError(err.message);
            alert('Failed to cancel booking: ' + err.message);
        } finally {
            setCancellingBooking(null);
        }
    };

    const openGoogleMaps = (hotelName, city) => {
        const query = encodeURIComponent(`${hotelName}, ${city}`);
        window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
    };

    const openPropertyLocation = (location) => {
        const query = encodeURIComponent(location);
        window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
    };

    const getStatusBadgeClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'confirmed': return 'status-confirmed';
            case 'pending': return 'status-pending';
            case 'checked-in': return 'status-checked-in';
            case 'checked-out': return 'status-checked-out';
            case 'cancelled': return 'status-cancelled';
            case 'completed': return 'status-completed';
            default: return 'status-pending';
        }
    };

    const getStatusText = (status) => {
        if (!status) return 'Pending';
        return status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ');
    };

    const calculateNights = (checkIn, checkOut) => {
        return Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24));
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="bookings-loading">
                <div className="loading-spinner"></div>
                <p>Loading your bookings...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bookings-error">
                <div className="error-icon">⚠️</div>
                <h3>Something went wrong</h3>
                <p>{error}</p>
                <button onClick={() => window.location.reload()} className="retry-btn">
                    Try Again
                </button>
            </div>
        );
    }

    const totalBookings = bookings.length + propertyBookings.length + purchaseBookings.length + serviceBookings.length;

    return (
        <div className="bookings-container">
            <div className="bookings-header">
                <h1>My Bookings</h1>
                <p className="bookings-subtitle">
                    You have {totalBookings} booking{totalBookings !== 1 ? 's' : ''} in total
                </p>
            </div>

            <div className="bookings-tabs">
                <button
                    className={`tab-btn ${activeTab === 'hotels' ? 'active' : ''}`}
                    onClick={() => setActiveTab('hotels')}
                >
                    <FaHotel className="tab-icon" />
                    Hotel Bookings
                    <span className="tab-count">{bookings.length}</span>
                </button>
                <button
                    className={`tab-btn ${activeTab === 'properties' ? 'active' : ''}`}
                    onClick={() => setActiveTab('properties')}
                >
                    <FaBuilding className="tab-icon" />
                    Property Bookings
                    <span className="tab-count">{propertyBookings.length + purchaseBookings.length}</span>
                </button>
                <button
                    className={`tab-btn ${activeTab === 'services' ? 'active' : ''}`}
                    onClick={() => setActiveTab('services')}
                >
                    <FaTools className="tab-icon" />
                    Service Bookings
                    <span className="tab-count">{serviceBookings.length}</span>
                </button>
            </div>

            {/* Hotel Bookings Tab */}
            {activeTab === 'hotels' && (
                <div className="bookings-content">
                    {bookings.length === 0 ? (
                        <div className="no-bookings">
                            <div className="no-bookings-icon">
                                <FaHotel />
                            </div>
                            <h3>No Hotel Bookings</h3>
                            <p>You haven't made any hotel bookings yet.</p>
                            <button
                                className="explore-btn"
                                onClick={() => window.location.href = '/hotels'}
                            >
                                Explore Hotels
                            </button>
                        </div>
                    ) : (
                        <div className="bookings-grid">
                            {bookings.map(booking => (
                                <div key={booking._id} className="booking-card hotel-booking">
                                    <div className="booking-card-header">
                                        <div className="booking-title-section">
                                            <h3 className="booking-title">{booking.hotel.name}</h3>
                                            <button
                                                className="location-btn"
                                                onClick={() => openGoogleMaps(booking.hotel.name, booking.hotel.city)}
                                                title="View on Google Maps"
                                            >
                                                <FaMapMarkerAlt />
                                                <span>View On Maps</span>
                                                <FaExternalLinkAlt className="external-icon" />
                                            </button>
                                        </div>
                                        <div className="booking-meta">
                                            <span className={`status-badge ${getStatusBadgeClass(booking.status)}`}>
                                                {getStatusText(booking.status)}
                                            </span>
                                            <div className="booking-price">
                                                XAF {booking.totalPrice.toLocaleString()}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="booking-details">
                                        <div className="detail-row">
                                            <FaBed className="detail-icon" />
                                            <div className="detail-info">
                                                <label>Room Category</label>
                                                <span className="room-category">{booking.room.type} Room</span>
                                                <small>{booking.room.pricePerNight.toLocaleString()} XAF per night</small>
                                            </div>
                                        </div>

                                        <div className="detail-row">
                                            <FaCalendarAlt className="detail-icon" />
                                            <div className="detail-info">
                                                <label>Stay Duration</label>
                                                <span className="date-range">
                                                    {formatDate(booking.checkInDate)} - {formatDate(booking.checkOutDate)}
                                                </span>
                                                <small>{calculateNights(booking.checkInDate, booking.checkOutDate)} nights</small>
                                            </div>
                                        </div>

                                        <div className="detail-row">
                                            <FaMoneyBillWave className="detail-icon" />
                                            <div className="detail-info">
                                                <label>Booking Details</label>
                                                <span>Booked on {formatDate(booking.createdAt)}</span>
                                            </div>
                                        </div>

                                        {booking.hotel.city && (
                                            <div className="detail-row">
                                                <FaMapMarkerAlt className="detail-icon" />
                                                <div className="detail-info">
                                                    <label>Location</label>
                                                    <span className="location-text">{booking.hotel.city}</span>
                                                    {booking.hotel.address && (
                                                        <small>{booking.hotel.address}</small>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="booking-actions">
                                        {booking.status === 'confirmed' && (
                                            <button
                                                className="btn btn-cancel"
                                                onClick={() => handleCancelBooking(
                                                    booking.hotel._id,
                                                    booking.room._id,
                                                    booking._id
                                                )}
                                                disabled={cancellingBooking === booking._id}
                                            >
                                                {cancellingBooking === booking._id ? 'Cancelling...' : 'Cancel Booking'}
                                            </button>
                                        )}
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => openGoogleMaps(booking.hotel.name, booking.hotel.city)}
                                        >
                                            <FaMapMarkerAlt />
                                            View On Maps
                                        </button>
                                        {(booking.status === 'checked-in' || booking.status === 'checked-out') && (
                                            <button className="btn btn-secondary">
                                                View Receipt
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Property Bookings Tab */}
            {activeTab === 'properties' && (
                <div className="bookings-content">
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
                        <select
                            value={propertyTabType}
                            onChange={(e) => setPropertyTabType(e.target.value)}
                            className={"bookings-dropdown"}
                            style={{ padding: '10px', borderRadius: '8px', border: '1px solid #ccc', outline: 'none' }}
                        >
                            <option value="rent">Property Room Bookings (Rent)</option>
                            <option value="buy">Property Bookings (Buy)</option>
                        </select>
                    </div>

                    {propertyTabType === 'rent' ? (
                        propertyBookings.length === 0 ? (
                            <div className="no-bookings">
                                <div className="no-bookings-icon">
                                    <FaBuilding />
                                </div>
                                <h3>No Property Room Bookings</h3>
                                <p>You haven't made any property room bookings yet.</p>
                                <button
                                    className="explore-btn"
                                    onClick={() => window.location.href = '/rent'}
                                >
                                    Explore Properties
                                </button>
                            </div>
                        ) : (
                            <div className="bookings-grid">
                                {propertyBookings.map(booking => (
                                    <div key={booking._id} className="booking-card property-booking">
                                        <div className="booking-card-header">
                                            <div className="booking-title-section">
                                                <h3 className="booking-title">{booking.room?.property?.data?.name || 'Property'}</h3>
                                                {booking.room?.property?.data?.location && (
                                                    <button
                                                        className="location-btn"
                                                        onClick={() => openPropertyLocation(booking.room.property.data.location)}
                                                        title="View on Google Maps"
                                                    >
                                                        <FaMapMarkerAlt />
                                                        <span>View Location</span>
                                                        <FaExternalLinkAlt className="external-icon" />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="booking-meta">
                                                <span className={`status-badge ${getStatusBadgeClass(booking.status)}`}>
                                                    {getStatusText(booking.status)}
                                                </span>
                                                <div className="booking-price">
                                                    XAF {booking.room?.price?.toLocaleString() || 0}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="booking-details">
                                            <div className="detail-row">
                                                <FaBuilding className="detail-icon" />
                                                <div className="detail-info">
                                                    <label>Property Type</label>
                                                    <span className="room-category">{booking.room?.roomType || 'N/A'}</span>
                                                    <small>{booking.room?.price?.toLocaleString() || 0} XAF per month</small>
                                                </div>
                                            </div>

                                            <div className="detail-row">
                                                <FaCalendarAlt className="detail-icon" />
                                                <div className="detail-info">
                                                    <label>Rental Period</label>
                                                    <span className="date-range">{booking.month}</span>
                                                </div>
                                            </div>

                                            {booking.room?.property?.user && (
                                                <div className="detail-row">
                                                    <FaUser className="detail-icon" />
                                                    <div className="detail-info">
                                                        <label>Property Owner</label>
                                                        <span>{booking.room.property.user.first_name}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {booking.room?.property?.data?.location && (
                                                <div className="detail-row">
                                                    <FaMapMarkerAlt className="detail-icon" />
                                                    <div className="detail-info">
                                                        <label>Property Location</label>
                                                        <span className="location-text">{booking.room.property.data.location}</span>
                                                        {booking.room.property.data.floors && (
                                                            <small>{booking.room.property.data.floors} floors</small>
                                                        )}
                                                    </div>
                                                </div>
                                            )}

                                            {booking.room?.property?.user && (
                                                <div className="detail-row">
                                                    <div className="detail-info full-width">
                                                        <label>Contact Owner</label>
                                                        <div className="contact-options">
                                                            {booking.room.property.data?.phone_number && (
                                                                <a
                                                                    href={`https://wa.me/237${booking.room.property.data.phone_number}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="contact-btn whatsapp"
                                                                >
                                                                    <FaWhatsapp />
                                                                    WhatsApp
                                                                </a>
                                                            )}
                                                            {booking.room.property.data?.phone_number && (
                                                                <a
                                                                    href={`tel:${booking.room.property.data.phone_number}`}
                                                                    className="contact-btn phone"
                                                                >
                                                                    <FaPhone />
                                                                    Call
                                                                </a>
                                                            )}
                                                            <a
                                                                href={`mailto:${booking.room.property.user.email}`}
                                                                className="contact-btn email"
                                                            >
                                                                <FaEnvelope />
                                                                Email
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="booking-actions">
                                            {booking.room?.property?.data?.location && (
                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={() => openPropertyLocation(booking.room.property.data.location)}
                                                >
                                                    <FaMapMarkerAlt />
                                                    View Location
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    ) : (
                        purchaseBookings.length === 0 ? (
                            <div className="no-bookings">
                                <div className="no-bookings-icon">
                                    <FaBuilding />
                                </div>
                                <h3>No Property Purchase Bookings</h3>
                                <p>You haven't made any property purchase requests yet.</p>
                                <button
                                    className="explore-btn"
                                    onClick={() => window.location.href = '/buy'}
                                >
                                    Explore Properties
                                </button>
                            </div>
                        ) : (
                            <div className="bookings-grid">
                                {purchaseBookings.map(booking => (
                                    <div key={booking._id} className="booking-card property-booking">
                                        <div className="booking-card-header">
                                            <div className="booking-title-section">
                                                <h3 className="booking-title">{booking.property?.data?.name || 'Property Purchase'}</h3>
                                                {booking.property?.data?.location && (
                                                    <button
                                                        className="location-btn"
                                                        onClick={() => openPropertyLocation(booking.property.data.location)}
                                                        title="View on Google Maps"
                                                    >
                                                        <FaMapMarkerAlt />
                                                        <span>View Location</span>
                                                        <FaExternalLinkAlt className="external-icon" />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="booking-meta">
                                                <span className={`status-badge ${getStatusBadgeClass(booking.status)}`}>
                                                    {getStatusText(booking.status)}
                                                </span>
                                                <div className="booking-price">
                                                    XAF {booking.offerAmount ? booking.offerAmount.toLocaleString() : (booking.property?.price?.toLocaleString() || 0)}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="booking-details">
                                            <div className="detail-row">
                                                <FaBuilding className="detail-icon" />
                                                <div className="detail-info">
                                                    <label>Property Category</label>
                                                    <span className="room-category">{booking.property?.category?.name || 'Land'}</span>
                                                    <small>Asking Price: XAF {booking.property?.price?.toLocaleString() || 0}</small>
                                                </div>
                                            </div>

                                            {booking.visitingDate && (
                                                <div className="detail-row">
                                                    <FaCalendarAlt className="detail-icon" />
                                                    <div className="detail-info">
                                                        <label>Preferred Visit</label>
                                                        <span className="date-range">{booking.visitingDate} {booking.visitingTime && `at ${booking.visitingTime}`}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {booking.property?.user && (
                                                <div className="detail-row">
                                                    <FaUser className="detail-icon" />
                                                    <div className="detail-info">
                                                        <label>Property Owner</label>
                                                        <span>{booking.property.user.first_name}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {booking.property?.data?.location && (
                                                <div className="detail-row">
                                                    <FaMapMarkerAlt className="detail-icon" />
                                                    <div className="detail-info">
                                                        <label>Property Location</label>
                                                        <span className="location-text">{booking.property.data.location}</span>
                                                    </div>
                                                </div>
                                            )}

                                            {booking.property?.user && (
                                                <div className="detail-row">
                                                    <div className="detail-info full-width">
                                                        <label>Contact Owner</label>
                                                        <div className="contact-options">
                                                            {booking.property.data?.phone_number && (
                                                                <a
                                                                    href={`https://wa.me/237${booking.property.data.phone_number}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="contact-btn whatsapp"
                                                                >
                                                                    <FaWhatsapp />
                                                                    WhatsApp
                                                                </a>
                                                            )}
                                                            {booking.property.data?.phone_number && (
                                                                <a
                                                                    href={`tel:${booking.property.data.phone_number}`}
                                                                    className="contact-btn phone"
                                                                >
                                                                    <FaPhone />
                                                                    Call
                                                                </a>
                                                            )}
                                                            <a
                                                                href={`mailto:${booking.property.user.email}`}
                                                                className="contact-btn email"
                                                            >
                                                                <FaEnvelope />
                                                                Email
                                                            </a>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="booking-actions">
                                            {booking.property?.data?.location && (
                                                <button
                                                    className="btn btn-secondary"
                                                    onClick={() => openPropertyLocation(booking.property.data.location)}
                                                >
                                                    <FaMapMarkerAlt />
                                                    View Location
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )}
                </div>
            )}

            {/* Service Bookings Tab */}
            {activeTab === 'services' && (
                <div className="bookings-content">
                    {serviceBookings.length === 0 ? (
                        <div className="no-bookings">
                            <div className="no-bookings-icon">
                                <FaTools />
                            </div>
                            <h3>No Service Bookings</h3>
                            <p>You haven't booked any services yet.</p>
                            <button
                                className="explore-btn"
                                onClick={() => window.location.href = '/serviceProvider'}
                            >
                                Explore Services
                            </button>
                        </div>
                    ) : (
                        <div className="bookings-grid">
                            {serviceBookings.map(booking => (
                                <div key={booking._id} className="booking-card service-booking">
                                    <div className="booking-card-header">
                                        <div className="booking-title-section">
                                            <h3 className="booking-title">{booking.offerId?.title || 'Service'}</h3>
                                        </div>
                                        <div className="booking-meta">
                                            <span className={`status-badge ${getStatusBadgeClass(booking.status)}`}>
                                                {getStatusText(booking.status)}
                                            </span>
                                            <div className="booking-price">
                                                XAF {(booking.paymentAmount || 0).toLocaleString()}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="booking-details">
                                        <div className="detail-row">
                                            <FaCalendarAlt className="detail-icon" />
                                            <div className="detail-info">
                                                <label>Date & Time</label>
                                                <span className="date-range">
                                                    {formatDate(booking.date)} at {booking.timeSlot}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="detail-row">
                                            <FaMapMarkerAlt className="detail-icon" />
                                            <div className="detail-info">
                                                <label>Service Location</label>
                                                <span className="location-text">{booking.location}</span>
                                            </div>
                                        </div>

                                        {booking.status === 'confirmed' && (
                                            <div className="detail-row">
                                                <FaUser className="detail-icon" />
                                                <div className="detail-info">
                                                    <label>Provider</label>
                                                    <span>{booking.providerId?.name || 'Contact details available once confirmed'}</span>
                                                    {booking.providerId?.email && (
                                                        <small>{booking.providerId.email}</small>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {booking.status === 'cancelled' && booking.refundStatus !== 'none' && (
                                            <div className="detail-row">
                                                <FaMoneyBillWave className="detail-icon" />
                                                <div className="detail-info">
                                                    <label>Refund</label>
                                                    <span>
                                                        {booking.refundStatus === 'completed' && 'Refunded'}
                                                        {booking.refundStatus === 'pending' && 'Refund in progress'}
                                                        {booking.refundStatus === 'failed' && 'Refund failed — contact support'}
                                                        {booking.refundStatus === 'not_applicable' && 'No charge was made'}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Bookings;